// ───────────────────────────────────────────────────────────────────────────
// Sidebar "unseen" layer.
//
// The sidebar badges used to show the TOTAL number of actionable records in a
// section (see src/hooks/useAdminActionBadges.ts). That number never went down
// until the underlying operational problem was solved, so a reviewed-but-still-
// unresolved item shouted at the admin forever.
//
// This module keeps the SAME actionable universe and adds a per-administrator
// acknowledgment layer on top of it:
//
//   • every actionable record becomes an UnseenItem with a stable `key`
//   • each item carries a `revision` fingerprint built ONLY from fields whose
//     change is meaningful ("a new document was submitted", "the payroll period
//     closed"). Re-running a scheduled checker, or an unchanged overdue date,
//     produces the SAME revision, so it never becomes unread again.
//   • a badge counts items with no acknowledgment for THIS admin, or whose
//     revision moved past the one they acknowledged.
//
// Acknowledgment is READ status only. It never resolves, approves, pays,
// completes or dismisses anything.
// ───────────────────────────────────────────────────────────────────────────

import { supabase } from "@/integrations/supabase/client";
import type { ActionBadgeKey } from "@/hooks/useAdminActionBadges";

export type UnseenBadgeKey = ActionBadgeKey;

export interface UnseenItem {
  badge: UnseenBadgeKey;
  /** Stable across refreshes and recalculations. */
  key: string;
  /** Changes only when something meaningfully new happened to this item. */
  revision: string;
  label: string;
  createdAt?: string | null;
  /**
   * The record already carries its own explicit unread flag (e.g. an Open
   * House registration with review_status = 'new'). The launch baseline must
   * NOT silently acknowledge these — that genuine unread state is preserved.
   */
  preserveUnread?: boolean;
}

export interface AckRecord {
  item_key: string;
  acked_revision: string;
}

/** Items this admin has not seen, or has seen at an older revision. */
export function computeUnseen(
  items: UnseenItem[],
  acks: Map<string, string>,
): UnseenItem[] {
  return items.filter((i) => acks.get(i.key) !== i.revision);
}

export function countUnseenByBadge(
  items: UnseenItem[],
  acks: Map<string, string>,
): Partial<Record<UnseenBadgeKey, number>> {
  const out: Partial<Record<UnseenBadgeKey, number>> = {};
  for (const i of computeUnseen(items, acks)) {
    out[i.badge] = (out[i.badge] ?? 0) + 1;
  }
  return out;
}

/** Items a first-time baseline may silently acknowledge. */
export function baselineAckables(items: UnseenItem[]): UnseenItem[] {
  return items.filter((i) => !i.preserveUnread);
}

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

const LIVE_ASSIGNMENT_STATUSES = ["active", "upcoming", "future", "current"];
const ACTIVE_WAITING_STATUSES = [
  "waiting", "reminder_scheduled", "reminder_sent",
  "response_received", "escalated", "overdue",
] as const;


async function safe(key: UnseenBadgeKey, fn: () => Promise<UnseenItem[]>): Promise<UnseenItem[]> {
  try {
    return await fn();
  } catch (e) {
    console.warn(`[admin unseen] "${key}" unavailable — showing no badge.`, e);
    return [];
  }
}

/* ------------------------------- loaders ------------------------------- */

async function enrollmentAlertItems(): Promise<UnseenItem[]> {
  const { data: checks, error } = await supabase
    .from("enrollment_min_checks")
    .select("id, class_title, class_slug, session_label, checkpoint, paid_enrolled, admin_action, below_minimum, created_at")
    .eq("below_minimum", true)
    .eq("admin_action", "none");
  if (error) throw error;
  const { data: alerts, error: aErr } = await supabase
    .from("class_interest_alerts")
    .select("id, class_title, class_slug, distinct_families, threshold, cleared_at, created_at")
    .is("cleared_at", null);
  if (aErr) throw aErr;
  return [
    ...(checks ?? []).map((c) => ({
      badge: "enrollment_alerts" as const,
      key: `emc:${c.id}`,
      // A scheduled re-check that finds the SAME enrollment at the SAME
      // checkpoint keeps the same revision → no fresh notification.
      revision: `${c.checkpoint}|${c.paid_enrolled}|${c.admin_action}`,
      label: `${c.class_title ?? c.class_slug} — below minimum (${c.checkpoint})`,
      createdAt: c.created_at,
    })),
    ...(alerts ?? []).map((a) => ({
      badge: "enrollment_alerts" as const,
      key: `cia:${a.id}`,
      revision: `interest|${a.distinct_families}`,
      label: `${a.class_title ?? a.class_slug} — ${a.distinct_families} interested families`,
      createdAt: a.created_at,
    })),
  ];
}

async function openHouseItems(): Promise<UnseenItem[]> {
  const { data, error } = await supabase
    .from("open_house_registrations")
    .select("id, first_name, last_name, review_status, follow_up_status, created_at")
    .or("review_status.eq.new,follow_up_status.eq.needed");
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    badge: "open_house" as const,
    key: `ohr:${r.id}`,
    revision: `${r.review_status}|${r.follow_up_status}`,
    label: `${[r.first_name, r.last_name].filter(Boolean).join(" ") || "Open house registration"} — ${r.review_status === "new" ? "new registration" : "follow-up needed"}`,
    createdAt: r.created_at,
    // review_status 'new' is the section's own existing unread flag.
    preserveUnread: r.review_status === "new",
  }));
}


async function instructorItems(): Promise<UnseenItem[]> {
  const { data: proposals, error } = await supabase
    .from("instructor_proposals")
    .select("id, full_name, proposed_class_name, status, review_status, created_at")
    .eq("status", "New");
  if (error) throw error;
  const { data: packets, error: pErr } = await supabase
    .from("instructor_onboarding_packets")
    .select("id, instructor_id, overall_status, submitted_at, review_content_fingerprint, created_at")
    .eq("overall_status", "submitted_for_review");
  if (pErr) throw pErr;
  return [
    ...(proposals ?? []).map((p: any) => ({
      badge: "instructors" as const,
      key: `prop:${p.id}`,
      revision: `${p.status}|${p.review_status}`,
      label: `${p.full_name} — proposal: ${p.proposed_class_name}`,
      createdAt: p.created_at,
      preserveUnread: p.review_status === "New",
    })),
    ...(packets ?? []).map((p: any) => ({
      badge: "instructors" as const,
      key: `pkt:${p.id}`,
      // The packet's own content fingerprint already means "documents changed".
      revision: `${p.overall_status}|${p.review_content_fingerprint ?? p.submitted_at ?? ""}`,
      label: "Onboarding packet submitted for review",
      createdAt: p.created_at,
    })),
  ];
}

async function payrollItems(): Promise<UnseenItem[]> {
  const { data, error } = await supabase
    .from("payroll_periods")
    .select("id, period_start, period_end, pay_date, status, created_at")
    .neq("status", "closed")
    .lte("pay_date", inDays(14));
  if (error) throw error;
  return (data ?? []).map((p: any) => ({
    badge: "payroll" as const,
    key: `pp:${p.id}`,
    // Staying overdue one more day is NOT new information — the pay date and
    // status are unchanged, so the revision is unchanged.
    revision: `${p.status}|${p.pay_date}`,
    label: `Payroll period ${p.period_start} – ${p.period_end} (pay date ${p.pay_date})`,
    createdAt: p.created_at,
  }));
}

async function waitingItems(): Promise<UnseenItem[]> {
  const { data, error } = await supabase
    .from("waiting_items")
    .select("id, person_name, reason, status, created_at")
    .in("status", ACTIVE_WAITING_STATUSES);
  if (error) throw error;
  return (data ?? []).map((w: any) => ({
    badge: "waiting_on" as const,
    key: `wi:${w.id}`,
    revision: `${w.status}`,
    label: `${w.person_name} — ${w.reason ?? "waiting"}`,
    createdAt: w.created_at,
  }));
}

async function classesNeedingInstructorItems(): Promise<UnseenItem[]> {
  const { data: rows, error } = await supabase
    .from("classes")
    .select("id, slug, title")
    .eq("status", "published")
    .eq("accepts_registrations", true)
    .or("hidden.is.null,hidden.eq.false");
  if (error) throw error;
  const open = rows ?? [];
  if (!open.length) return [];
  const { data: assigns, error: aErr } = await supabase
    .from("instructor_class_assignments")
    .select("class_id, class_slug, status, end_date")
    .in("status", LIVE_ASSIGNMENT_STATUSES);
  if (aErr) throw aErr;
  const stamp = today();
  const staffed = new Set<string>();
  for (const a of assigns ?? []) {
    if (a.end_date && a.end_date < stamp) continue;
    if (a.class_id) staffed.add(a.class_id);
    if (a.class_slug) staffed.add(a.class_slug);
  }
  return open
    .filter((c) => !staffed.has(c.id) && !staffed.has(c.slug))
    .map((c) => ({
      badge: "classes" as const,
      key: `cls:${c.id}`,
      revision: "unstaffed",
      label: `${c.title} — open for registration with no instructor`,
    }));
}

async function brokenCheckoutItems(): Promise<UnseenItem[]> {
  const { data, error } = await supabase
    .from("checkout_failure_log")
    .select("id, class_name, error_code, parent_charged, retry_succeeded, created_at")
    .eq("parent_charged", true)
    .not("retry_succeeded", "is", true);
  if (error) throw error;
  return (data ?? []).map((f: any) => ({
    badge: "registrations" as const,
    key: `cfl:${f.id}`,
    revision: `${f.error_code}|${f.retry_succeeded ?? "null"}`,
    label: `${f.class_name ?? "Checkout"} — parent charged, enrollment failed`,
    createdAt: f.created_at,
  }));
}

async function ledgerItems(): Promise<UnseenItem[]> {
  const { data, error } = await supabase
    .from("financial_integrity_state")
    .select("id, needs_reconciliation, unresolved_count, last_event_reason, updated_at");
  if (error) throw error;
  return (data ?? [])
    .filter((r: any) => r.needs_reconciliation || (r.unresolved_count ?? 0) > 0)
    .map((r: any) => ({
      badge: "ledger" as const,
      key: `fis:${r.id}`,
      revision: `${r.needs_reconciliation}|${r.unresolved_count ?? 0}`,
      label: r.last_event_reason ?? "Reconciliation finding",
    }));
}

/** Loads the full actionable universe across every badged section. */
export async function loadUnseenUniverse(): Promise<UnseenItem[]> {
  const groups = await Promise.all([
    safe("enrollment_alerts", enrollmentAlertItems),
    safe("open_house", openHouseItems),
    safe("instructors", instructorItems),
    safe("payroll", payrollItems),
    safe("waiting_on", waitingItems),
    safe("classes", classesNeedingInstructorItems),
    safe("registrations", brokenCheckoutItems),
    safe("ledger", ledgerItems),
  ]);
  return groups.flat();
}

/** Every badge this layer knows how to count. */
export const UNSEEN_BADGES: UnseenBadgeKey[] = [
  "waiting_on", "classes", "enrollment_alerts", "registrations",
  "open_house", "instructors", "payroll", "ledger",
];
